import './KwelloIconButton.scss';
import { Button } from '@progress/kendo-react-buttons';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import { useId, useRef, useState } from 'react';
import { Spinner } from '../loader/Spinner';
import BodyTooltip, { BodyTooltipPosition } from '../BodyTooltip/BodyTooltip';

interface KwelloIconButtonProps {
  id?: string;
  iconId?: string;
  className?: string;
  icon: KWELLO_ICON;
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
  size?: 'small' | 'medium' | 'large';
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  fillMode?: 'flat' | 'outline' | 'link' | 'solid' | 'clear';
  count?: number;
  isLoading?: boolean;
  isError?: boolean;
  active?: boolean;
  bodyTooltip?: boolean;
}

function KwelloIconButton({
  id,
  iconId,
  className,
  icon,
  onClick,
  disabled,
  tooltip,
  size = 'large',
  tooltipPosition = 'top',
  fillMode = 'flat',
  count = -1,
  isLoading = false,
  isError = false,
  active = false,
  bodyTooltip = false,
}: Readonly<KwelloIconButtonProps>) {
  const buttonRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();
  const tooltipRef = useRef<TooltipRefProps>(null);
  const [tipPosition, setTipPosition] = useState<BodyTooltipPosition | undefined>();

  const showBodyTooltip = () => {
    if (!bodyTooltip) {
      return;
    }

    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setTipPosition({ x: rect.left + rect.width / 2, y: rect.top - 15 });
    }
  };

  const hideBodyTooltip = () => {
    if (!bodyTooltip) {
      return;
    }

    setTipPosition(undefined);
    if (tooltipRef.current) {
      tooltipRef.current.close();
    }
  };

  return (
    <span
      ref={buttonRef}
      data-tooltip-id={tooltipId}
      onMouseEnter={showBodyTooltip}
      onMouseLeave={hideBodyTooltip}
    >
      <Button
        id={id}
        fillMode={fillMode}
        size={size}
        className={`k-icon-button size-${size} ${className ?? ''} ${active ? 'active' : ''}`}
        onClick={isLoading ? undefined : onClick}
        disabled={disabled || isLoading}
      >
        {isLoading && <Spinner size="small" />}
        {!isLoading && (
          <>
            <KwelloSvgIcon id={iconId} icon={icon} className="k-icon k-svg-icon k-button-icon" />
            {!isError && count > -1 && <span className="k-button-count">{count}</span>}
            {isError && (
              <KwelloSvgIcon
                id={iconId}
                icon={KWELLO_ICON.WARNING_TRIANGLE_ICON}
                className="k-icon k-svg-icon button-error-icon"
              />
            )}
          </>
        )}
      </Button>
      {bodyTooltip && (
        <BodyTooltip showTooltip={!!tipPosition} position={tipPosition}>
          {tooltip}
        </BodyTooltip>
      )}

      {!bodyTooltip && (
        <Tooltip
          id={tooltipId}
          place={tooltipPosition}
          positionStrategy={'fixed'}
          style={{ fontSize: '0.8rem' }}
        >
          {tooltip}
        </Tooltip>
      )}
    </span>
  );
}

export default KwelloIconButton;
